"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from "react";
import CatalogLookCard from "./CatalogLookCard";
import CatalogSectionBanner from "./CatalogSectionBanner";
import CatalogToolCard from "./CatalogToolCard";
import {
  getHubNav,
  getHubSections,
  HUB_BANNER_TONES,
  hubCardKey,
  isHubSectionId,
  isPhotoEditorJtbdId,
  normalizeHubHash,
  PHOTO_EDITOR_PILLAR_ID,
  resolveHubScrollId,
  type HubBanner,
  type HubCard,
  type HubJtbdSection,
  type HubLooksSection,
  type HubNavItem,
} from "./hubData";

const PILL_OFFSET_PX = 80;

function readHash(): string {
  if (typeof window === "undefined") return "";
  return normalizeHubHash(decodeURIComponent(window.location.hash.replace(/^#/, "")));
}

function setHash(id: string, mode: "push" | "replace") {
  const next = `#${encodeURIComponent(id)}`;
  if (window.location.hash === next) return;
  const url = `${window.location.pathname}${window.location.search}${next}`;
  if (mode === "push") window.history.pushState(null, "", url);
  else window.history.replaceState(null, "", url);
}

function scrollToId(id: string, behavior: ScrollBehavior) {
  document.getElementById(id)?.scrollIntoView({ behavior, block: "start" });
}

function isPillarActive(item: HubNavItem, activeId: string): boolean {
  if (item.id === activeId) return true;
  if (item.id !== PHOTO_EDITOR_PILLAR_ID) return false;
  return isPhotoEditorJtbdId(activeId);
}

function HubCardView({ card, sectionId }: { card: HubCard; sectionId: string }) {
  if (card.type === "tool") {
    return <CatalogToolCard tool={card.tool} />;
  }
  return (
    <CatalogLookCard
      href={card.href}
      title={card.title}
      tagline={card.tagline}
      images={card.images}
      toolId={card.toolId}
      cardType={card.cardType}
      cardId={card.id}
      section={sectionId}
      overlaySrc={card.overlaySrc}
      borderFrames={card.borderFrames}
    />
  );
}

export default function PhotoEditorCatalogDashboard({
  hero,
  hideLeadBanner = false,
}: {
  /** Replaces the default headline block above the pill nav. */
  hero?: ReactNode;
  /** Skip the first looks banner when the hero already covers it. */
  hideLeadBanner?: boolean;
} = {}) {
  const sections = useMemo(() => getHubSections(), []);
  const nav = useMemo(() => getHubNav(sections), [sections]);
  const lookSections = sections.filter(
    (section): section is HubLooksSection => section.kind === "looks" && !section.stealth
  );
  const stealthLookSections = sections.filter(
    (section): section is HubLooksSection => section.kind === "looks" && Boolean(section.stealth)
  );
  const editorSection = sections.find((section) => section.kind === "editor");
  const jtbdSections = sections.filter((section): section is HubJtbdSection => section.kind === "jtbd");

  const [activeId, setActiveId] = useState(sections[0]?.id ?? "");
  const activeIdRef = useRef(activeId);
  activeIdRef.current = activeId;
  const lockUntil = useRef(0);
  const pillRefs = useRef<Record<string, HTMLAnchorElement | null>>({});
  const pillRowRef = useRef<HTMLDivElement | null>(null);
  const filterSentinelRef = useRef<HTMLDivElement | null>(null);
  const [filterStuck, setFilterStuck] = useState(false);

  const activate = useCallback(
    (id: string, mode: "push" | "replace", behavior: ScrollBehavior) => {
      if (!isHubSectionId(id, sections)) return;
      const scrollId = resolveHubScrollId(id, nav);
      lockUntil.current = Date.now() + 900;
      setActiveId(id);
      setHash(id, mode);
      scrollToId(scrollId, behavior);
    },
    [nav, sections]
  );

  useLayoutEffect(() => {
    const fromUrl = readHash();
    if (fromUrl && isHubSectionId(fromUrl, sections)) {
      lockUntil.current = Date.now() + 900;
      setActiveId(fromUrl);
      scrollToId(resolveHubScrollId(fromUrl, nav), "auto");
      const raw = decodeURIComponent(window.location.hash.replace(/^#/, "")).trim();
      if (raw !== fromUrl) setHash(fromUrl, "replace");
    }
  }, [nav, sections]);

  useEffect(() => {
    const onHash = () => {
      const id = readHash();
      if (!id || !isHubSectionId(id, sections)) return;
      lockUntil.current = Date.now() + 900;
      setActiveId(id);
      scrollToId(resolveHubScrollId(id, nav), "auto");
    };
    window.addEventListener("hashchange", onHash);
    window.addEventListener("popstate", onHash);
    return () => {
      window.removeEventListener("hashchange", onHash);
      window.removeEventListener("popstate", onHash);
    };
  }, [nav, sections]);

  useEffect(() => {
    const ids = [
      ...lookSections.map((section) => section.id),
      ...stealthLookSections.map((section) => section.id),
      editorSection?.id,
      ...jtbdSections.map((section) => section.id),
    ].filter((id): id is string => Boolean(id));
    const nodes = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);
    if (nodes.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (Date.now() < lockUntil.current) return;
        const hit = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
        const id = hit?.target.id;
        if (!id || id === activeIdRef.current) return;
        setActiveId(id);
        setHash(id, "replace");
      },
      { rootMargin: `-${PILL_OFFSET_PX}px 0px -55% 0px`, threshold: 0.01 }
    );

    nodes.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [editorSection?.id, jtbdSections, lookSections, stealthLookSections]);

  useEffect(() => {
    const pill =
      pillRefs.current[activeId] ??
      (isPhotoEditorJtbdId(activeId) ? pillRefs.current[PHOTO_EDITOR_PILLAR_ID] : null);
    const row = pillRowRef.current;
    if (!pill || !row) return;
    const pillMid = pill.offsetLeft + pill.offsetWidth / 2;
    const target = pillMid - row.clientWidth / 2;
    row.scrollTo({ left: Math.max(0, target), behavior: "smooth" });
  }, [activeId]);

  useEffect(() => {
    const sentinel = filterSentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(([entry]) => {
      setFilterStuck(!entry.isIntersecting);
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  return (
    <div>
      {hero ?? (
        <div className="mb-8">
          <h1
            className="text-3xl font-bold text-gray-900 md:text-4xl lg:text-5xl"
            style={{ fontFamily: "var(--font-merriweather)" }}
          >
            One selfie. Every version of you.
          </h1>
          <p className="mt-2 text-base text-gray-500 md:text-lg">
            Professional headshots, creative photoshoots, and style tools - all from one selfie.
          </p>
        </div>
      )}

      <div ref={filterSentinelRef} className="h-0" aria-hidden />
      <div
        className={`sticky z-40 bg-white/95 backdrop-blur top-0 -mx-4 rounded-none border-x-0 sm:top-3 sm:mx-3 sm:rounded-3xl sm:border ${
          filterStuck
            ? "border-b border-gray-200 shadow-sm sm:border sm:shadow-md"
            : "border-b border-transparent sm:border-gray-200/80"
        }`}
      >
        <div
          ref={pillRowRef}
          className="flex items-center gap-2 overflow-x-auto px-4 py-1.5 sm:px-2"
          style={{ scrollbarWidth: "none" }}
        >
          {nav.pillars.map((item) => (
            <FilterPill
              key={item.id}
              item={item}
              selected={isPillarActive(item, activeId) && !isPhotoEditorJtbdId(activeId)}
              parentActive={item.id === PHOTO_EDITOR_PILLAR_ID && isPhotoEditorJtbdId(activeId)}
              pillRefs={pillRefs}
              onActivate={activate}
            />
          ))}
          <div className="mx-1 hidden h-6 w-px shrink-0 bg-gray-200 sm:block" aria-hidden />
          {nav.jtbds.map((item) => (
            <FilterPill
              key={item.id}
              item={item}
              selected={item.id === activeId}
              pillRefs={pillRefs}
              onActivate={activate}
            />
          ))}
        </div>
      </div>

      <div className="space-y-16 pt-8 pb-8 md:space-y-20">
        {lookSections.map((section, index) => (
          <LooksPillar
            key={section.id}
            section={section}
            priority={index === 0}
            hideBanner={hideLeadBanner && index === 0}
          />
        ))}

        {stealthLookSections.map((section) => (
          <LooksPillar key={section.id} section={section} priority={false} />
        ))}

        {editorSection?.kind === "editor" ? (
          <section id={editorSection.id} style={{ scrollMarginTop: PILL_OFFSET_PX }}>
            <PillarBanner banner={editorSection.banner} />
          </section>
        ) : null}

        {jtbdSections.map((section) => (
          <JtbdBlock key={section.id} section={section} />
        ))}
      </div>
    </div>
  );
}

function FilterPill({
  item,
  selected,
  parentActive = false,
  pillRefs,
  onActivate,
}: {
  item: HubNavItem;
  selected: boolean;
  parentActive?: boolean;
  pillRefs: MutableRefObject<Record<string, HTMLAnchorElement | null>>;
  onActivate: (id: string, mode: "push" | "replace", behavior: ScrollBehavior) => void;
}) {
  const isPillar = item.kind === "pillar";
  return (
    <a
      ref={(el) => {
        pillRefs.current[item.id] = el;
      }}
      href={`#${item.id}`}
      onClick={(e) => {
        e.preventDefault();
        onActivate(item.id, "push", "smooth");
      }}
      className={`inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border font-semibold transition-colors ${
        isPillar ? "py-1.5 pl-1.5 pr-3.5 text-sm" : "px-3.5 py-2 text-sm"
      } ${
        selected
          ? "border-primary bg-primary text-white"
          : parentActive
            ? "border-primary bg-primary/10 text-primary"
            : "border-gray-200 bg-white text-gray-700 hover:border-primary/40 hover:text-primary"
      }`}
    >
      {isPillar && item.thumb ? (
        <img
          src={item.thumb}
          alt=""
          className="h-7 w-7 rounded-full object-cover object-top"
          draggable={false}
        />
      ) : null}
      {!isPillar && item.emoji ? (
        <span className="text-[15px] leading-none" aria-hidden>
          {item.emoji}
        </span>
      ) : null}
      <span>{item.label}</span>
    </a>
  );
}

function PillarBanner({ banner, priority = false }: { banner: HubBanner; priority?: boolean }) {
  return (
    <div className="mb-8 lg:mb-10">
      <CatalogSectionBanner banner={banner} priority={priority} />
    </div>
  );
}

function LooksPillar({
  section,
  priority,
  hideBanner = false,
}: {
  section: HubLooksSection;
  priority: boolean;
  hideBanner?: boolean;
}) {
  return (
    <section id={section.id} style={{ scrollMarginTop: PILL_OFFSET_PX }}>
      {hideBanner ? null : <PillarBanner banner={section.banner} priority={priority} />}
      <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-4 md:gap-x-6 md:gap-y-10 lg:grid-cols-6">
        {section.cards.map((card) => (
          <HubCardView key={hubCardKey(card, section.id)} card={card} sectionId={section.id} />
        ))}
      </div>
      <div className="mt-8 flex justify-center md:mt-10">
        <a
          href={section.viewAllHref}
          className="inline-flex items-center justify-center rounded-full px-7 py-3 text-sm font-semibold text-white shadow-sm transition hover:brightness-110 hover:shadow-md md:px-8 md:text-base"
          style={{ backgroundColor: HUB_BANNER_TONES[section.banner.tone].fill }}
        >
          {section.viewAllLabel}
        </a>
      </div>
    </section>
  );
}

function JtbdBlock({ section }: { section: HubJtbdSection }) {
  return (
    <section id={section.id} style={{ scrollMarginTop: PILL_OFFSET_PX }}>
      <div className="mb-5 flex items-end justify-between gap-4">
        <div>
          <h2
            className="text-2xl font-bold text-gray-800 md:text-3xl"
            style={{ fontFamily: "var(--font-merriweather)" }}
          >
            {section.emoji ? (
              <span className="mr-2" aria-hidden>
                {section.emoji}
              </span>
            ) : null}
            {section.title}
          </h2>
          {section.tagline ? (
            <p className="mt-1 text-sm text-gray-500 md:text-base">{section.tagline}</p>
          ) : null}
        </div>
        {section.viewAllHref && section.viewAllLabel ? (
          <a
            href={section.viewAllHref}
            className="whitespace-nowrap text-sm font-semibold text-primary hover:underline md:text-base"
          >
            {section.viewAllLabel}
          </a>
        ) : null}
      </div>
      {section.cards.length === 0 ? (
        <p className="text-sm text-gray-500">No presets in this category yet.</p>
      ) : (
        <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-x-6 md:gap-y-10 lg:grid-cols-5">
          {section.cards.map((card) => (
            <HubCardView key={hubCardKey(card, section.id)} card={card} sectionId={section.id} />
          ))}
        </div>
      )}
    </section>
  );
}
